import React, { useState, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import PublishStatusComponent from '../../components/PublishStatusComponent';
import { useToast } from '../../providers/ToastProvider';
import { Session } from '../../peer-colab-model/Session';
import { InsuranceProviderManagement } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';
import { InsuranceProvider } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/InsuranceProviders/PathItems';
import { Published } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Core/PathItems';

interface EditInsuranceProviderProps {
  id: string;
  onBack: () => void;
}

function EditInsuranceProvider({ id, onBack }: EditInsuranceProviderProps) {
  const { onError } = useToast();
  const [provider, setProvider] = useState<InsuranceProvider | null>(null);
  const [name, setName] = useState('');
  const [state, setState] = useState<Published>({ state: 'draft', isDirty: false });

  const loadProvider = async () => {
    onError((await Session.getClient().addPathParam('insuranceProviderId', id).request(InsuranceProviderManagement.getInsuranceProvider()))
      .maybeOk((prov: InsuranceProvider) => {
        if (prov) {
          setProvider(prov);
          setName(prov.name);
          setState(prov.state);
        }
      }));
  };

  useEffect(() => {
    loadProvider();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function patchField<T>(
    operation: any,
    newValue: T,
    currentValue: T,
    revertHandler: (value: T) => void
  ): Promise<void> {
    if (newValue === currentValue) return;
    const client = Session.getClient().addPathParam('insuranceProviderId', id);
    onError((await client.request(operation)).maybeOk(loadProvider));
  }

  if (!provider) return null;

  return (
    <Box sx={{ p: 3 }}>
      <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mb: 2 }}>
        <ArrowBackIcon />
      </IconButton>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Insurance Provider Details
        </Typography>
        <PublishStatusComponent
          status={state}
          onTransition={async (action) => {
            return (await Session.getClient().addPathParam('insuranceProviderId', id).request(InsuranceProviderManagement.updateInsuranceProviderState({ stateTransition: action })))
              .maybeOk(() => loadProvider());
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 350, maxWidth: 500 }}>
        <TextField
          label="Provider Name"
          value={name}
          onChange={e => setName(e.target.value)}
          onBlur={() => patchField(InsuranceProviderManagement.updateInsuranceProviderName({ name }), name, provider.name, setName)}
          sx={{ mb: 2 }}
        />
      </Box>
    </Box>
  );
}

export default EditInsuranceProvider;
